import { Link } from "react-router-dom";
import { motion } from "framer-motion";

interface BlogTag {
  id: string;
  name: string;
  slug: string;
}

interface BlogCardProps {
  title: string;
  slug: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  publishedAt: string | null;
  authorName: string;
  tags: BlogTag[];
}

export function BlogCard({ title, slug, excerpt, coverImageUrl, publishedAt, authorName, tags }: BlogCardProps) {
  const formattedDate = publishedAt
    ? new Date(publishedAt).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="group"
    >
      <Link to={`/blog/${slug}`} className="block">
        {/* Cover Image */}
        <div className="aspect-[16/10] overflow-hidden bg-muted mb-4">
          {coverImageUrl ? (
            <img
              src={coverImageUrl}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-secondary">
              <span className="text-muted-foreground text-sm">אין תמונה</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {tags.map(tag => (
              <span
                key={tag.id}
                className="text-xs tracking-wide text-primary/70 border border-border px-2 py-0.5"
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h2 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors duration-300 mb-2 leading-tight">
          {title}
        </h2>

        {/* Excerpt */}
        {excerpt && (
          <p className="text-sm text-muted-foreground leading-relaxed mb-3 line-clamp-2">
            {excerpt}
          </p>
        )}

        {/* Meta */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{authorName}</span>
          {formattedDate && (
            <>
              <span className="text-border">|</span>
              <time dateTime={publishedAt || ''}>{formattedDate}</time>
            </>
          )}
        </div>
      </Link>
    </motion.article>
  );
}
